import type { GroupKey } from "@common/types/todo";
import { Flavor } from "@common/utils/type-utils";
import type { DraggableId, DroppableGroupId, DroppableId } from "@web/src/app/use-cases/id-flavors";
import { EventEmitter } from "eventemitter3";
import { action, computed, makeAutoObservable, observable } from "mobx";
import type { MutableRefObject } from "react";
import React from "react";

export const DroppableGroupContext = React.createContext({ droppableGroupId: "" });

export type DropPositioning = { x: number /** 0 for sibling, 1 for child, negative number for deparent */; y: -1 | 1 };

export type Coordinates = { x: number; y: number };

export const DndEvents = new EventEmitter<{
  ON_POINTER_MOVE: (pointerPosition: Coordinates) => void;
}>();

export interface DraggableData<TDraggableKinds extends string> {
  kind: TDraggableKinds;
}

export interface DroppableData<TDroppableKinds extends string> {
  kind: TDroppableKinds;
  droppableId: DroppableId;
  groupId: DroppableGroupId;
  canDrop?: () => boolean;
}

export type DndTypeBundle<
  TDraggableKinds extends string = string,
  TDroppableKinds extends string = string,
  TDraggableData extends DraggableData<TDraggableKinds> = DraggableData<TDraggableKinds>,
  TDroppableData extends DroppableData<TDroppableKinds> = DroppableData<TDroppableKinds>
> = {
  TDroppableKinds: TDroppableKinds;
  TDraggableKinds: TDraggableKinds;
  TDraggableData: TDraggableData;
  TDroppableData: TDroppableData;
};

export function groupKeyToDroppableGroupId(groupKey: GroupKey): DroppableGroupId {
  return `${groupKey}`;
}

export function droppableGroupIdToGroupKey(droppableGroupId: DroppableGroupId | null): GroupKey {
  return `${droppableGroupId}`;
}

export type DragEndParams<TDndTypeBundle extends DndTypeBundle> = {
  position: { x: number; y: -1 | 1 }; // y is before / after, x is sibling / child / (grand)parent's siblings
  sourceId: DraggableId;
  targetId: DroppableId;
  groupId: DroppableGroupId | null;
  dndStore: DndStore<TDndTypeBundle>;
};

export interface DroppableProvider<TDndTypeBundle extends DndTypeBundle> {
  kind: TDndTypeBundle["TDroppableKinds"];
  handleDrop(params: DragEndParams<TDndTypeBundle>): Promise<void> | void;
}

export class DndStore<TDndTypeBundle extends DndTypeBundle<string, string, any, any>> {
  @observable
  isAutoscrolling = false;
  @observable
  isPrimed = false;
  @observable
  isDragging = false;
  @observable
  currentDraggableId: DraggableId | null = null;

  @computed
  get currentDraggableData(): TDndTypeBundle["TDraggableData"] | null {
    const draggableId = this.currentDraggableId;
    if (!draggableId) return null;
    return this.draggableDataMap.get(draggableId) ?? null;
  }

  draggableRef: MutableRefObject<HTMLElement | null> | null = null;
  draggableDataMap = new Map<DraggableId, TDndTypeBundle["TDraggableData"]>();

  @observable
  droppableTargetId: DroppableId | null = null;
  @observable
  droppableTargetKind: TDndTypeBundle["TDroppableKinds"] | null = null;
  @observable
  droppableGroupId: DroppableGroupId | null = null;
  droppableDataMap = new Map<DroppableId, TDndTypeBundle["TDroppableData"]>();
  @observable
  droppableInsertPosition: DropPositioning | null = null;

  droppableContainerRefMap = new Map<DroppableId, MutableRefObject<HTMLElement | null>>();
  droppableGroupContainerRefMap = new Map<DroppableGroupId, MutableRefObject<HTMLElement | null>>();

  @observable
  pointerPosition = { x: 0, y: 0 };
  @observable
  pointerOffset = { x: 0, y: 0 };
  @observable
  translation = { x: 0, y: 0 };

  scrollContainer: MutableRefObject<HTMLElement | null> | null = null;

  constructor(readonly dropHandlers: Record<TDndTypeBundle["TDroppableKinds"], DroppableProvider<TDndTypeBundle>>) {
    makeAutoObservable(this);
  }

  @action
  setScrollContainer(ref: MutableRefObject<HTMLElement | null>) {
    this.scrollContainer = ref;
  }

  @action
  setCurDraggable(id: DraggableId, ref: MutableRefObject<HTMLElement | null>) {
    this.currentDraggableId = id;
    this.draggableRef = ref;
  }

  @action
  addTranslation(translation: Coordinates) {
    this.translation = { x: this.translation.x + translation.x, y: this.translation.y + translation.y };
  }

  @action
  setPointerPosition(position: Coordinates) {
    this.pointerPosition = position;
  }

  @action
  setPointerOffset(offset: Coordinates) {
    this.pointerOffset = offset;
  }

  @action
  resetTranslation() {
    this.translation = { x: 0, y: 0 };
  }

  @action
  registerDraggable(id: DraggableId, draggableData: TDndTypeBundle["TDraggableData"]) {
    const map = this.draggableDataMap;
    map.set(id, draggableData);
  }

  @action
  deregisterDraggable(id: DraggableId) {
    const map = this.draggableDataMap;
    map.delete(id);
  }

  @action
  registerDroppable(
    id: DroppableId,
    droppableData: TDndTypeBundle["TDroppableData"],
    ref: MutableRefObject<HTMLElement | null>
  ) {
    this.droppableContainerRefMap.set(id, ref);
    this.droppableDataMap.set(id, droppableData);
  }

  @action
  deregisterDroppable(id: DroppableId) {
    this.droppableContainerRefMap.delete(id);
    this.droppableDataMap.delete(id);
  }

  @action
  registerGroupContainerRef(groupId: DroppableGroupId, ref: MutableRefObject<HTMLElement | null>) {
    this.droppableGroupContainerRefMap.set(groupId, ref);
  }

  @action
  deregisterGroupContainerRef(groupId: DroppableGroupId) {
    this.droppableGroupContainerRefMap.delete(groupId);
  }

  @action
  setDroppableTarget(
    targetId: DroppableId,
    kind: TDndTypeBundle["TDroppableKinds"],
    droppableGroupId: DroppableGroupId,
    position: DropPositioning
  ) {
    this.droppableTargetId = targetId;
    this.droppableTargetKind = kind;
    this.droppableInsertPosition = position;
    this.droppableGroupId = droppableGroupId;
  }

  @action
  clearDroppableTarget() {
    this.droppableTargetId = null;
    this.droppableGroupId = null;
    this.droppableInsertPosition = null;
  }

  @action
  setIsPrimed(isPrimed: boolean) {
    this.isPrimed = isPrimed;
  }

  @action
  setIsDragging(isDragging: boolean) {
    this.isDragging = isDragging;
  }

  @action
  reset() {
    this.droppableTargetId = null;
    this.droppableGroupId = null;
    this.droppableInsertPosition = null;
    this.isPrimed = false;
    this.currentDraggableId = null;
    this.draggableRef = null;
    this.translation = { x: 0, y: 0 };
  }

  @action
  setIsAutoscrolling(isAutoscrolling: boolean) {
    this.isAutoscrolling = isAutoscrolling;
  }

  @action
  handleDrop(droppableKind: TDndTypeBundle["TDroppableKinds"], dropData: DragEndParams<TDndTypeBundle>) {
    this.dropHandlers[droppableKind].handleDrop(dropData);
  }
}
